import { useQueryClient } from '@tanstack/react-query';
import { Loader2, Mail, Pencil, Plus, Send, Trash2 } from 'lucide-react';
import { useState } from 'react';

import {
  getAlertasAdminControllerListarDestinatariosQueryKey,
  useAlertasAdminControllerAtualizarDestinatario,
  useAlertasAdminControllerCriarDestinatario,
  useAlertasAdminControllerEnviarTeste,
  useAlertasAdminControllerExcluirDestinatario,
  useAlertasAdminControllerListarDestinatarios,
} from '@/api/generated/admin-alertas/admin-alertas';
import type { DestinatarioDeAlertaResponse } from '@/api/generated/model';
import { ApiError } from '@/api/http-client';
import { dataHora } from '@/features/admin/alertas/rotulos';
import { EstadoErro, EstadoVazio } from '@/shared/components/estados';
import { usePermissoes } from '@/shared/permissoes/usePermissoes';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/ui/alert-dialog';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Skeleton } from '@/shared/ui/skeleton';
import { toast } from '@/shared/ui/sonner';
import { Switch } from '@/shared/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table';

/**
 * Quem recebe o e-mail de alerta crítico.
 *
 * ⚠️ Permissão própria (`admin_alertas_destinatarios.*`), que nenhum cargo
 * recebe no seed: na prática só o super admin vê esta aba. Cada botão aparece
 * conforme a ação que a pessoa tem.
 */
export function DestinatariosTab() {
  const { pode } = usePermissoes();
  const queryClient = useQueryClient();
  const { data, isPending, isError, refetch } = useAlertasAdminControllerListarDestinatarios();
  const atualizar = useAlertasAdminControllerAtualizarDestinatario();
  const excluir = useAlertasAdminControllerExcluirDestinatario();
  const enviarTeste = useAlertasAdminControllerEnviarTeste();

  const [formAberto, setFormAberto] = useState(false);
  const [emEdicao, setEmEdicao] = useState<DestinatarioDeAlertaResponse | undefined>();
  const [aExcluir, setAExcluir] = useState<DestinatarioDeAlertaResponse | null>(null);
  const [testando, setTestando] = useState<string | null>(null);

  const recarregar = () =>
    queryClient.invalidateQueries({ queryKey: getAlertasAdminControllerListarDestinatariosQueryKey() });

  async function alternarAtivo(destinatario: DestinatarioDeAlertaResponse, ativo: boolean) {
    try {
      await atualizar.mutateAsync({ id: destinatario.id, data: { ativo } });
      await recarregar();
      toast.success(ativo ? `${destinatario.email} volta a receber alertas.` : `${destinatario.email} não recebe mais alertas.`);
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Não foi possível atualizar o destinatário.');
    }
  }

  async function confirmarExclusao() {
    if (!aExcluir) return;
    try {
      await excluir.mutateAsync({ id: aExcluir.id });
      await recarregar();
      toast.success(`${aExcluir.email} removido.`);
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Não foi possível remover o destinatário.');
    } finally {
      setAExcluir(null);
    }
  }

  async function testar(destinatario: DestinatarioDeAlertaResponse) {
    setTestando(destinatario.id);
    try {
      await enviarTeste.mutateAsync({ id: destinatario.id });
      toast.success(`E-mail de teste enviado para ${destinatario.email}.`);
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Não foi possível enviar o e-mail de teste.');
    } finally {
      setTestando(null);
    }
  }

  const ativos = data?.itens.filter((d) => d.ativo).length ?? 0;

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          Recebem um e-mail quando um alerta fica <strong className="text-foreground">crítico</strong> (no máximo um
          por hora para o mesmo alerta).
          {data && ativos === 0 && (
            <span className="block text-destructive">Nenhum destinatário ativo: os alertas críticos não avisam ninguém.</span>
          )}
        </p>
        {pode('admin_alertas_destinatarios.criar') && (
          <Button
            onClick={() => {
              setEmEdicao(undefined);
              setFormAberto(true);
            }}
          >
            <Plus className="w-4 h-4 mr-2" />
            Adicionar e-mail
          </Button>
        )}
      </div>

      <div className="bg-surface-raised rounded-lg border-2 border-brand/20 overflow-hidden">
        {isPending ? (
          <div className="p-6 space-y-3">
            {[0, 1].map((i) => (
              <Skeleton key={i} className="h-10 rounded-md" />
            ))}
          </div>
        ) : isError || !data ? (
          <EstadoErro aoTentarDeNovo={() => void refetch()} />
        ) : data.itens.length === 0 ? (
          <EstadoVazio icone={Mail} titulo="Nenhum destinatário" descricao="Adicione quem deve receber os alertas críticos." />
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>E-mail</TableHead>
                  <TableHead>Nome</TableHead>
                  <TableHead>Recebe alertas</TableHead>
                  <TableHead>Cadastrado em</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.itens.map((destinatario) => (
                  <TableRow key={destinatario.id}>
                    <TableCell className="font-medium text-foreground">{destinatario.email}</TableCell>
                    <TableCell>{destinatario.nome ?? '—'}</TableCell>
                    <TableCell>
                      {pode('admin_alertas_destinatarios.editar') ? (
                        <Switch
                          checked={destinatario.ativo}
                          onCheckedChange={(ativo) => void alternarAtivo(destinatario, ativo)}
                          aria-label={`${destinatario.ativo ? 'Desativar' : 'Ativar'} ${destinatario.email}`}
                        />
                      ) : (
                        <Badge variant="outline">{destinatario.ativo ? 'Sim' : 'Não'}</Badge>
                      )}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-muted-foreground">
                      {dataHora(destinatario.criadoEm)}
                    </TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      {pode('admin_alertas_destinatarios.editar') && (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => void testar(destinatario)}
                            disabled={testando === destinatario.id}
                            aria-label={`Enviar teste para ${destinatario.email}`}
                            title="Enviar e-mail de teste"
                          >
                            {testando === destinatario.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Send className="w-4 h-4" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setEmEdicao(destinatario);
                              setFormAberto(true);
                            }}
                            aria-label={`Editar ${destinatario.email}`}
                            title="Editar"
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                      {pode('admin_alertas_destinatarios.excluir') && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setAExcluir(destinatario)}
                          aria-label={`Remover ${destinatario.email}`}
                          title="Remover"
                        >
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {formAberto && (
        <DestinatarioFormDialogo
          {...(emEdicao ? { destinatario: emEdicao } : {})}
          aoFechar={() => setFormAberto(false)}
          aoSalvar={() => void recarregar()}
        />
      )}

      <AlertDialog open={Boolean(aExcluir)} onOpenChange={(v) => !v && setAExcluir(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remover {aExcluir?.email}?</AlertDialogTitle>
            <AlertDialogDescription>
              Este endereço deixa de receber os e-mails de alerta crítico. Para só pausar, use a chave "Recebe
              alertas".
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => void confirmarExclusao()}>Remover</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function DestinatarioFormDialogo({
  destinatario,
  aoFechar,
  aoSalvar,
}: {
  destinatario?: DestinatarioDeAlertaResponse;
  aoFechar: () => void;
  aoSalvar: () => void;
}) {
  const criar = useAlertasAdminControllerCriarDestinatario();
  const atualizar = useAlertasAdminControllerAtualizarDestinatario();
  const [email, setEmail] = useState(destinatario?.email ?? '');
  const [nome, setNome] = useState(destinatario?.nome ?? '');
  const [erro, setErro] = useState<string | null>(null);
  const salvando = criar.isPending || atualizar.isPending;

  async function salvar() {
    setErro(null);
    const dados = { email: email.trim(), nome: nome.trim() || null };
    try {
      if (destinatario) {
        await atualizar.mutateAsync({ id: destinatario.id, data: dados });
      } else {
        await criar.mutateAsync({ data: dados });
      }
      toast.success(destinatario ? 'Destinatário atualizado.' : `${dados.email} vai receber os alertas críticos.`);
      aoSalvar();
      aoFechar();
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : 'Não foi possível salvar.');
    }
  }

  return (
    <Dialog open onOpenChange={(v) => !v && aoFechar()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{destinatario ? 'Editar destinatário' : 'Novo destinatário'}</DialogTitle>
          <DialogDescription>Este e-mail recebe um aviso quando um alerta fica crítico.</DialogDescription>
        </DialogHeader>
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            void salvar();
          }}
        >
          <div className="space-y-2">
            <Label htmlFor="destinatario-email">E-mail</Label>
            <Input
              id="destinatario-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="destinatario-nome">Nome (opcional)</Label>
            <Input id="destinatario-nome" value={nome} onChange={(e) => setNome(e.target.value)} />
          </div>
          {erro && (
            <p role="alert" className="text-sm text-destructive">
              {erro}
            </p>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={aoFechar}>
              Cancelar
            </Button>
            <Button type="submit" disabled={salvando || !email.trim()}>
              {salvando && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Salvar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
